import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as _ from 'lodash-es';
import { Subscription } from 'rxjs';
import { AppService } from '../../../../app.service';
import { GlobalService } from '../../../../global/services/global.service';
import { GridEventService } from '../../services/grid-event.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
} from '../../../../shared/workspace-element-context';
import { ConfirmationModalConfig } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GridActionModalService } from './grid-action-modal.service';

@Component({
  selector: 'gtw-grid-action-modal',
  templateUrl: './grid-action-modal.component.html',
  styleUrls: ['./grid-action-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GridActionModalComponent implements OnInit, OnDestroy {
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();

  modalTitle = 'Grid Action Manager';
  minimise = false;
  formSubmitted = false;
  userAccessLevel = 0;

  actionList: any[] = [];
  selectedAction: any = null;
  action: any = {
    name: '',
    type: '',
    loading: false,
    system: 0,
    sign_off: 0,
  };

  /** From activeScreen.data.gridData — gizmoGrid: { templateArr, tableState, cellData } */
  gridModalData: any = {};
  GENERAL_CONFIG: any = {};

  showDeleteConfirmModal = false;
  deleteConfirmConfig: ConfirmationModalConfig = {
    title: 'Grid Action',
    message: 'Are you sure you want to permanently delete this Grid Action',
    iconClass: 'fa fa-exclamation-triangle',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
  };

  private readonly loadActonTemplatesListener = (data: any) => {
    if (Array.isArray(data)) {
      this.actionList = data;
      this.cdr.markForCheck();
    }
  };

  @Input('grid-data')
  set _gridData(gridData: string) {
    try {
      const parsed = typeof gridData === 'string' ? JSON.parse(gridData) : gridData;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        this.gridModalData = parsed;
        this.cdr.markForCheck();
      }
    } catch {
      // Input not yet stable.
    }
  }

  @Input('action-templates')
  set _actionTemplates(actionTemplates: string) {
    try {
      const parsed = typeof actionTemplates === 'string' ? JSON.parse(actionTemplates) : actionTemplates;
      if (Array.isArray(parsed)) {
        this.actionList = parsed;
        this.cdr.markForCheck();
      }
    } catch {
      // Input not yet stable.
    }
  }

  constructor(
    private readonly gtwGlobal: GlobalService,
    private readonly gtwWorkspace: WorkspaceService,
    private readonly gtwApp: AppService,
    private readonly gridActionModalService: GridActionModalService,
    private readonly gridEventService: GridEventService,
    private readonly snackBar: MatSnackBar,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
    }));
    this.gridEventService.on(
      'loadActonTemplates',
      'grid-action-modal',
      this.loadActonTemplatesListener,
    );
  }

  ngOnDestroy(): void {
    this.gridEventService.off('loadActonTemplates', this.loadActonTemplatesListener);
    this.subs.unsubscribe();
  }

  toggleMinimise(): void {
    this.minimise = !this.minimise;
    this.cdr.markForCheck();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);

    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.userAccessLevel = this.gtwApp.USER_SETTINGS?.user?.system_access_level ?? 0;

    const templates = activeScreen?.data?.actionTemplates;
    if (Array.isArray(templates)) {
      this.actionList = templates;
    }

    const gridData = activeScreen?.data?.gridData;
    if (gridData && typeof gridData === 'object' && !Array.isArray(gridData)) {
      this.gridModalData = gridData;
    }

    this.cdr.markForCheck();
  }

  private getActiveScreen(): any {
    return this.gtwWorkspace.workspaceFactory?.activeScreen;
  }

  private getBaseUrl(): string {
    return this.GENERAL_CONFIG?.base_url || generalConfigFromGlobal(this.gtwGlobal)?.api || '';
  }

  setType(type: 'edit' | 'new'): void {
    const activeScreen = this.getActiveScreen();
    const pivotGroup = activeScreen?.data?.pivotGroup;

    if (type === 'edit') {
      if (!this.selectedAction) {
        this.openSnack('Please select a Grid Action', 'gtw-snackbar--yellow');
        return;
      }
      this.formSubmitted = false;
      this.action = this.prepareActionForForm(this.selectedAction);
      this.action.type = 'edit';
      this.cdr.markForCheck();
      return;
    }

    const tableState = this.gridModalData?.tableState;
    const hasPivot =
      pivotGroup &&
      ((pivotGroup.columnToRows && pivotGroup.columnToRows.length > 0) ||
        (pivotGroup.groupRows && pivotGroup.groupRows.length > 0));

    if (!this.checkForFilters(tableState) && !hasPivot) {
      this.openSnack(
        'You do not have any filters set with this action. You must apply one or more filters to set an action template',
        'gtw-snackbar--yellow',
      );
      return;
    }

    this.formSubmitted = false;
    this.action = {
      name: '',
      type: 'new',
      loading: false,
      system: 0,
      sign_off: 0,
      description: '',
    };
    this.selectedAction = null;
    this.cdr.markForCheck();
  }

  private prepareActionForForm(source: any): any {
    const action = { ...source };
    action.system = action.system == 1 ? 1 : 0;
    action.sign_off = action.sign_off == 1 ? 1 : 0;
    if (action.signOffDate != null && action.signOffDate !== '') {
      action.signOffDate = this.normalizeDateForInput(action.signOffDate);
    }
    return action;
  }

  private normalizeDateForInput(value: any): string {
    if (!value) {
      return '';
    }
    if (value instanceof Date && !isNaN(value.getTime())) {
      return value.toISOString().slice(0, 10);
    }
    if (typeof value === 'string') {
      const mdyMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (mdyMatch) {
        const month = mdyMatch[1].padStart(2, '0');
        const day = mdyMatch[2].padStart(2, '0');
        return `${mdyMatch[3]}-${month}-${day}`;
      }
      if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
        return value.slice(0, 10);
      }
    }
    return '';
  }

  private checkForFilters(tableState: any): boolean {
    if (!tableState) {
      return false;
    }
    return (
      !_.isEmpty(tableState.colSearch) ||
      !_.isEmpty(tableState.search) ||
      !_.isEmpty(tableState.sort)
    );
  }

  onSystemChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.action.system = checked ? 1 : 0;
    if (!checked) {
      this.action.sign_off = 0;
    }
    this.cdr.markForCheck();
  }

  onSignOffChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.action.sign_off = checked ? 1 : 0;
    this.cdr.markForCheck();
  }

  openSignOffDatePicker(): void {
    const dateInput = document.getElementById('grid_action_sign_off_date') as HTMLInputElement;
    if (!dateInput) {
      return;
    }

    dateInput.focus();
    const inputWithPicker = dateInput as HTMLInputElement & { showPicker?: () => void };
    if (typeof inputWithPicker.showPicker === 'function') {
      try {
        inputWithPicker.showPicker();
        return;
      } catch {
        // fall through to click()
      }
    }
    dateInput.click();
  }

  save(form: NgForm): void {
    this.formSubmitted = true;
    if (form.invalid) {
      form.control.markAllAsTouched();
      this.openSnack('Please correct the errors below', 'gtw-snackbar--red');
      this.cdr.markForCheck();
      return;
    }
    this.persist();
  }

  private persist(): void {
    const formatChecker = /[<>]/;
    if (formatChecker.test(this.action.name || '')) {
      this.openSnack('Do not use special characters like <, > for Action Name field', 'gtw-snackbar--red');
      return;
    }

    if (this.action.loading) {
      this.openSnack('Form has already been submitted. ', 'info-snackbar');
      return;
    }

    const activeScreen = this.getActiveScreen();
    const baseUrl = this.getBaseUrl();
    if (!baseUrl) {
      return;
    }

    this.action.loading = true;
    this.cdr.markForCheck();

    const params = this.buildPersistParams(activeScreen);

    this.gridActionModalService.saveGridActionTemplate(baseUrl, params).subscribe({
      next: (response: any) => {
        this.action.loading = false;
        if (response?.callSuccess != '1') {
          this.openSnack(
            'Error occurred saving template data. If this continues please contact support',
            'gtw-snackbar--red',
          );
          this.cdr.markForCheck();
          return;
        }

        this.openSnack('Action template was saved', 'gtw-snackbar--green');
        if (activeScreen?.data) {
          activeScreen.data.actionTemplates = response.actionTemplates;
        }
        if (response.actionTemplates) {
          this.actionList = response.actionTemplates;
          this.gridEventService.broadcast('loadActonTemplates', response.actionTemplates);
        }
        this.cancel();
      },
      error: () => {
        this.action.loading = false;
        this.openSnack(
          'Error occurred saving template data. If this continues please contact support',
          'gtw-snackbar--red',
        );
        this.cdr.markForCheck();
      },
    });
  }

  private buildPersistParams(activeScreen: any): Record<string, unknown> {
    const params: Record<string, unknown> = { ...this.action };

    params.loading = true;
    params.grid_key = activeScreen?.data?.settings?.grid_key ?? null;

    const pivotGroup = activeScreen?.data?.pivotGroup;
    const hasPivot =
      pivotGroup &&
      ((pivotGroup.columnToRows && pivotGroup.columnToRows.length > 0) ||
        (pivotGroup.groupRows && pivotGroup.groupRows.length > 0));

    if (hasPivot) {
      params.action_type = 'pivot';
      params.gridData = JSON.stringify(pivotGroup);
    } else {
      params.gridData = JSON.stringify(this.gridModalData);
    }

    return params;
  }

  confirmDelete(): void {
    this.showDeleteConfirmModal = true;
    this.cdr.markForCheck();
  }

  onConfirmDelete(form?: NgForm): void {
    this.showDeleteConfirmModal = false;
    if (form?.invalid) {
      this.formSubmitted = true;
      form.control.markAllAsTouched();
      this.openSnack('Please correct the errors below', 'gtw-snackbar--red');
      this.cdr.markForCheck();
      return;
    }
    this.action.type = 'delete';
    this.persist();
    this.cdr.markForCheck();
  }

  onCancelDelete(): void {
    this.showDeleteConfirmModal = false;
    this.cdr.markForCheck();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  private openSnack(message: string, panelClass: string): void {
    this.snackBar.open(message, 'Close', { duration: 4000, panelClass: [panelClass] });
  }
}
